import { useState, useEffect, useCallback, useRef } from 'react';
import { chatWebSocket, SignalResponse, SignalType } from '@/entities/chat/api/chatWebSocket';
import { ChatContact } from '@/entities/chat/model/types';
import { useToast } from '@/shared/ui/toast/ToastContext';
import { getApiErrorMessage } from "@/shared/lib/errorMessage";

export type CallState = 'IDLE' | 'STARTING' | 'RINGING' | 'CONNECTED' | 'ENDED' | 'RECONNECTING';
export type CallType = 'audio' | 'video';

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    {
      urls: "turn:nozomi.proxy.rlwy.net:20034?transport=tcp",
      username: "jose",
      credential: "senha123"
    }
  ],
  iceTransportPolicy: 'relay' as const
};

export const useCRMCall = (userId: string, activeContact: ChatContact | null, onCallEnd?: (duration: number, wasMissed: boolean) => void) => {
  const toast = useToast();
  const [callState, setCallState] = useState<CallState>('IDLE');
  const callStateRef = useRef<CallState>('IDLE');
  const [callType, setCallType] = useState<CallType>('audio');
  const [incomingCall, setIncomingCall] = useState<{ senderId: string; type: CallType } | null>(null);

  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  const peerConnections = useRef<Map<string, RTCPeerConnection>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const pendingCandidates = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());

  const [devices, setDevices] = useState<{ audioIn: MediaDeviceInfo[], videoIn: MediaDeviceInfo[], audioOut: MediaDeviceInfo[] }>({ audioIn: [], videoIn: [], audioOut: [] });
  const [selectedDevices, setSelectedDevices] = useState({ audioIn: '', videoIn: '', audioOut: '' });

  const updateCallState = useCallback((newState: CallState) => {
    if (callStateRef.current === newState) return;
    setCallState(newState);
    callStateRef.current = newState;
    
    if (newState === 'CONNECTED') {
        sessionStorage.setItem('crm_active_call', JSON.stringify({ topicId: activeContact?.id, type: callType, timestamp: Date.now() }));
    } else if (newState === 'IDLE' || newState === 'ENDED') {
        sessionStorage.removeItem('crm_active_call');
    }
  }, [activeContact, callType]);

  const cleanup = useCallback(() => {
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
    if (screenStreamRef.current) { screenStreamRef.current.getTracks().forEach(t => t.stop()); screenStreamRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
    
    peerConnections.current.forEach(pc => pc.close());
    peerConnections.current.clear();
    pendingCandidates.current.clear();
    
    if (callStateRef.current !== 'IDLE' && callStateRef.current !== 'ENDED') {
      updateCallState('ENDED');
      setTimeout(() => updateCallState('IDLE'), 2000);
    } else {
      updateCallState('IDLE');
    }
    
    setIncomingCall(null);
    setLocalStream(null);
    setRemoteStreams(new Map());
    setIsScreenSharing(false);
  }, [updateCallState]);

  const processAudio = useCallback((stream: MediaStream) => {
    if (stream.getAudioTracks().length === 0) return stream;
    try {
      const AudioContextClass = (window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as typeof AudioContext;
      const audioContext = audioContextRef.current || new AudioContextClass();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') audioContext.resume();
      const source = audioContext.createMediaStreamSource(stream);
      const destination = audioContext.createMediaStreamDestination();
      const hpf = audioContext.createBiquadFilter();
      hpf.type = 'highpass'; hpf.frequency.setValueAtTime(100, audioContext.currentTime);
      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-45, audioContext.currentTime);
      source.connect(hpf); hpf.connect(compressor); compressor.connect(destination);
      return new MediaStream([...destination.stream.getAudioTracks(), ...stream.getVideoTracks()]);
    } catch (e) { return stream; }
  }, []);

  const optimizeSDP = useCallback((sdp: string, isScreen: boolean = false) => {
    let modifiedSdp = sdp.replace('useinbandfec=1', 'useinbandfec=1;usedtx=1;maxaveragebitrate=32000');
    if (modifiedSdp.indexOf('m=video') > -1) {
      const bitrate = isScreen ? 1200 : 450;
      if (modifiedSdp.indexOf('b=AS:') === -1) modifiedSdp = modifiedSdp.replace(/(m=video.*\r\n)/g, `$1b=AS:${bitrate}\r\n`);
      else modifiedSdp = modifiedSdp.replace(/b=AS:(\d+)/g, `b=AS:${bitrate}`);
    }
    return modifiedSdp;
  }, []);

  const applySenderSettings = useCallback((pc: RTCPeerConnection, isScreen: boolean) => {
    pc.getSenders().forEach(s => {
      if (s.track?.kind === 'video') {
        const p = s.getParameters(); if (!p.encodings) p.encodings = [{}];
        // @ts-expect-error - legacy stream handling
        s.degradationPreference = isScreen ? 'maintain-resolution' : 'balanced';
        p.encodings[0].maxBitrate = isScreen ? 1200000 : 500000;
        s.setParameters(p).catch(() => {});
      }
    });
  }, []);

  const createPeerConnection = useCallback((targetId: string) => {
    if (peerConnections.current.has(targetId)) return peerConnections.current.get(targetId)!;
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    pc.onicecandidate = (e) => { if (e.candidate) chatWebSocket.sendWebRTCSignal(targetId, 'ICE_CANDIDATE', e.candidate); };
    
    pc.ontrack = (event) => {
      setRemoteStreams(prev => {
        const newMap = new Map(prev);
        const stream = newMap.get(targetId) || new MediaStream();
        if (!stream.getTracks().find(t => t.id === event.track.id)) stream.addTrack(event.track);
        newMap.set(targetId, stream);
        return newMap;
      });
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current!));
    }
    
    peerConnections.current.set(targetId, pc);
    return pc;
  }, []);

  const initiateOffer = useCallback(async (targetId: string, offerCallType: CallType = 'audio') => {
    const pc = createPeerConnection(targetId);
    const offer = await pc.createOffer();
    const optimized = new RTCSessionDescription({ type: offer.type, sdp: optimizeSDP(offer.sdp || '', false) });
    await pc.setLocalDescription(optimized);
    applySenderSettings(pc, false);
    chatWebSocket.sendWebRTCSignal(targetId, 'OFFER', { sdp: optimized.sdp, type: optimized.type, callType: offerCallType });
  }, [createPeerConnection, optimizeSDP, applySenderSettings]);

  const startCall = useCallback(async (contact: ChatContact, type: CallType, isRejoin = false) => {
    try {
      if (!isRejoin) updateCallState('STARTING');
      setCallType(type);
      const raw = await navigator.mediaDevices.getUserMedia({ audio: true, video: type === 'video' ? { width: 1280, height: 720, frameRate: 24 } : false });
      const stream = processAudio(raw); localStreamRef.current = stream; setLocalStream(stream);

      // Se for uma sala de voz, entramos nela em vez de ligar direto
      if (contact.topicType === 'GROUP' || contact.topicType === 'CALL_ROOM' as never) {
        chatWebSocket.subscribeToRoom(contact.id, (response) => {
            const { senderId, type, payload } = response;
            if (senderId === userId) return;

            if (type === 'ROOM_JOINED') {
                // Preparado para receber oferta
            } else if (type === 'ROOM_LEFT') {
                const pc = peerConnections.current.get(senderId);
                if (pc) {
                    pc.close();
                    peerConnections.current.delete(senderId);
                    setRemoteStreams(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(senderId);
                        return newMap;
                    });
                }
            }
        });
        chatWebSocket.joinRoom(contact.id);
        updateCallState('CONNECTED');
      } else {
        // Chamada direta 1:1
        const targets = contact.members?.map(m => m.id).filter(id => id !== userId) || [];
        for (const targetId of targets) {
          if (!targetId || String(targetId) === String(userId)) continue;
          await initiateOffer(targetId, type);
        }
      }
    } catch (e) { cleanup(); }
  }, [userId, cleanup, updateCallState, processAudio, initiateOffer]);

  const answerCall = useCallback(async () => {
    if (!incomingCall) return;
    const { senderId, type } = incomingCall; 
    updateCallState('CONNECTED'); 

    try {
      // Detectar se a chamada inclui vídeo: verificar o SDP remoto do PC existente
      let hasVideo = type === 'video';
      const existingPc = peerConnections.current.get(senderId);
      if (existingPc?.remoteDescription?.sdp) {
        hasVideo = hasVideo || existingPc.remoteDescription.sdp.includes('m=video');
      }
      
      const raw = await navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: hasVideo ? { width: 1280, height: 720, frameRate: 24 } : false 
      });
      const stream = processAudio(raw); localStreamRef.current = stream; setLocalStream(stream);
      
      // Reutilizar o PC existente (criado quando recebemos a OFFER)
      let pc = peerConnections.current.get(senderId);
      if (!pc) {
        console.warn('⚠️ PeerConnection não encontrada, criando nova...');
        pc = createPeerConnection(senderId);
      }
      
      // Injetar tracks locais nos transceivers existentes (criados pelo setRemoteDescription da OFFER)
      // Usar replaceTrack nos senders existentes em vez de addTrack, que criaria m-lines extras no SDP
      const transceivers = pc.getTransceivers();
      const usedTransceivers = new Set<RTCRtpTransceiver>();
      
      stream.getTracks().forEach(track => {
        // Procurar transceiver existente do mesmo tipo que ainda não tem track local
        const transceiver = transceivers.find(t => 
          t.receiver.track?.kind === track.kind && !t.sender.track && !usedTransceivers.has(t)
        );
        
        if (transceiver) {
          transceiver.sender.replaceTrack(track);
          transceiver.direction = 'sendrecv';
          usedTransceivers.add(transceiver);
        } else {
          // Fallback: tentar encontrar qualquer transceiver do mesmo tipo
          const fallback = transceivers.find(t => 
            t.sender.track?.kind === track.kind || (t.mid !== null && !usedTransceivers.has(t) && t.receiver.track?.kind === track.kind)
          );
          if (fallback && !fallback.sender.track) {
            fallback.sender.replaceTrack(track);
            fallback.direction = 'sendrecv';
            usedTransceivers.add(fallback);
          } else {
            pc!.addTrack(track, stream);
          }
        }
      });

      const answer = await pc.createAnswer();
      const optimized = new RTCSessionDescription({ type: answer.type, sdp: optimizeSDP(answer.sdp || '', false) });
      await pc.setLocalDescription(optimized); applySenderSettings(pc, false);
      chatWebSocket.sendWebRTCSignal(String(senderId), 'ANSWER', optimized);
      const candidates = pendingCandidates.current.get(senderId) || [];
      for (const cand of candidates) await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(() => {});
      pendingCandidates.current.delete(senderId);
    } catch (e) { console.error('❌ Erro ao atender chamada:', e); cleanup(); }
  }, [incomingCall, createPeerConnection, cleanup, updateCallState, processAudio, optimizeSDP, applySenderSettings]);

  useEffect(() => {
    if (!userId) return;
    chatWebSocket.subscribeToWebRTCSignals(async (response: SignalResponse) => {
      const { senderId, type } = response; if (senderId === userId) return;
      const payload = response.payload as unknown as RTCSessionDescriptionInit & RTCIceCandidateInit & { callType?: CallType; mute?: boolean; url?: string };
      switch (type) {
        case 'PARTICIPANT_LIST':
          const participantList = payload as unknown as Array<string | { userId?: string }>;
          const activeParticipants = participantList.map((p) => (typeof p === 'string' ? p : p.userId));
          for (const pId of activeParticipants) {
              if (pId && pId !== userId) await initiateOffer(pId, callType);
          }
          break;
        case 'OFFER':
          // Se NÓS iniciamos a chamada (STARTING), ignorar OFFERs recebidas — somos o chamador
          if (callStateRef.current === 'STARTING') {
            break;
          }
          
          // Criar PC e setar remote description ANTES de decidir auto-atender
          const pcO = createPeerConnection(senderId); 
          await pcO.setRemoteDescription(new RTCSessionDescription(payload));
          
          // Em salas de voz ou se já estamos conectados (grupo), auto-atender inline
          if (activeContact?.topicType === 'CALL_ROOM' as never || callStateRef.current === 'CONNECTED') {
            try {
              // Se ainda não temos stream local, capturar
              if (!localStreamRef.current) {
                const autoHasVideo = payload.callType === 'video' || (pcO.remoteDescription?.sdp?.includes('m=video') ?? false);
                const raw = await navigator.mediaDevices.getUserMedia({ 
                  audio: true, 
                  video: autoHasVideo ? { width: 1280, height: 720, frameRate: 24 } : false 
                });
                const stream = processAudio(raw); localStreamRef.current = stream; setLocalStream(stream);
              }
              // Injetar tracks nos transceivers existentes
              const transceivers = pcO.getTransceivers();
              const used = new Set<RTCRtpTransceiver>();
              localStreamRef.current.getTracks().forEach(track => {
                const tr = transceivers.find(t => t.receiver.track?.kind === track.kind && !t.sender.track && !used.has(t));
                if (tr) { tr.sender.replaceTrack(track); tr.direction = 'sendrecv'; used.add(tr); }
                else { pcO.addTrack(track, localStreamRef.current!); }
              });
              const answer = await pcO.createAnswer();
              const optimized = new RTCSessionDescription({ type: answer.type, sdp: optimizeSDP(answer.sdp || '', false) });
              await pcO.setLocalDescription(optimized); applySenderSettings(pcO, false);
              chatWebSocket.sendWebRTCSignal(String(senderId), 'ANSWER', optimized);
              const cands = pendingCandidates.current.get(senderId) || [];
              for (const c of cands) await pcO.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
              pendingCandidates.current.delete(senderId);
            } catch (e) { console.error('❌ Erro auto-atendendo em grupo:', e); }
          } else {
            setIncomingCall({ senderId, type: payload.callType || 'audio' }); 
            setCallType(payload.callType || 'audio');
            updateCallState('RINGING');
          }
          break;
        case 'ANSWER':
          const pcA = peerConnections.current.get(senderId);
          if (pcA && pcA.signalingState === 'have-local-offer') {
            await pcA.setRemoteDescription(new RTCSessionDescription(payload));
            updateCallState('CONNECTED');
          } else {
            console.warn(`⚠️ ANSWER ignorada — PC state: ${pcA?.signalingState || 'sem PC'}`);
          }
          break;
        case 'ICE_CANDIDATE':
          const pcI = peerConnections.current.get(senderId);
          if (pcI?.remoteDescription) await pcI.addIceCandidate(new RTCIceCandidate(payload)).catch(() => {});
          else { const l = pendingCandidates.current.get(senderId) || []; l.push(payload); pendingCandidates.current.set(senderId, l); } break;
        case 'HANG_UP':
          const pcH = peerConnections.current.get(senderId);
          if (pcH) {
              pcH.close();
              peerConnections.current.delete(senderId);
              setRemoteStreams(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(senderId);
                  return newMap;
              });
              if (peerConnections.current.size === 0 && activeContact?.topicType !== 'CALL_ROOM' as never) cleanup();
          }
          break;
        case 'SERVER_MUTE':
          if (payload.mute && localStreamRef.current) {
              localStreamRef.current.getAudioTracks().forEach(t => t.enabled = false);
              setIsMuted(true);
          } break;
        case 'PLAY_SOUND':
          const audio = new Audio(payload.url); audio.volume = 0.4; audio.play().catch(() => {}); break;
      }
    });
    return () => chatWebSocket.unsubscribe('/user/queue/call');
  }, [userId, createPeerConnection, cleanup, updateCallState, answerCall, initiateOffer, activeContact]);

  const toggleMute = useCallback(() => { if (localStreamRef.current) { const t = localStreamRef.current.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setIsMuted(!t.enabled); } } }, []);
  const toggleCamera = useCallback(() => { if (localStreamRef.current) { const t = localStreamRef.current.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setIsCameraOff(!t.enabled); } } }, []);

  const switchDevice = useCallback(async (kind: 'audioIn' | 'videoIn', deviceId: string) => {
    setSelectedDevices(prev => ({ ...prev, [kind]: deviceId }));
    if (!localStreamRef.current) return;
    try {
        const c = { audio: kind === 'audioIn' ? { deviceId } : true, video: kind === 'videoIn' ? { deviceId } : (callType === 'video') };
        const raw = await navigator.mediaDevices.getUserMedia(c);
        const stream = processAudio(raw);
        peerConnections.current.forEach(pc => {
            const senders = pc.getSenders();
            const aT = stream.getAudioTracks()[0]; if (aT) senders.find(s => s.track?.kind === 'audio')?.replaceTrack(aT);
            const vT = stream.getVideoTracks()[0]; if (vT) senders.find(s => s.track?.kind === 'video' && !isScreenSharing)?.replaceTrack(vT);
        });
        localStreamRef.current = stream; setLocalStream(stream);
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao trocar dispositivo")); }
  }, [callType, isScreenSharing, processAudio]);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
        if (screenStreamRef.current) { screenStreamRef.current.getTracks().forEach(t => t.stop()); screenStreamRef.current = null; }
        peerConnections.current.forEach(pc => {
            const s = pc.getSenders().find(sender => sender.track?.label?.includes('screen') || (sender.track?.kind === 'video' && sender.track !== localStreamRef.current?.getVideoTracks()[0]));
            if (s) pc.removeTrack(s);
        });
        setIsScreenSharing(false);
    } else {
        try {
            const ss = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: false });
            screenStreamRef.current = ss; const st = ss.getVideoTracks()[0];
            st.onended = () => toggleScreenShare();
            peerConnections.current.forEach(pc => { pc.addTrack(st, ss); applySenderSettings(pc, true); });
            setIsScreenSharing(true);
        } catch (e) {}
    }
  }, [isScreenSharing, applySenderSettings]);

  const leaveRoom = useCallback(() => {
    if (activeContact) {
        chatWebSocket.leaveRoom(activeContact.id);
        chatWebSocket.unsubscribe(`/topic/topic/${activeContact.id}/room`);
    }
    cleanup();
  }, [activeContact, cleanup]);

  const endCall = useCallback(() => { 
    if (activeContact?.topicType === 'CALL_ROOM' as never) {
        leaveRoom();
    } else {
        peerConnections.current.forEach((pc, tid) => chatWebSocket.sendWebRTCSignal(tid, 'HANG_UP', {}));
        cleanup(); 
    }
  }, [activeContact, leaveRoom, cleanup]);

  return { callState, callType, incomingCall, remoteStreams, localStream, isMuted, isCameraOff, isScreenSharing, devices, selectedDevices, startCall, answerCall, rejectCall: cleanup, endCall, toggleMute, toggleCamera, toggleScreenShare, switchDevice, sendSoundEffect: (url: string) => chatWebSocket.sendWebRTCSignal(activeContact?.id || '', 'PLAY_SOUND', { url }), sendServerMute: (tid: string, m: boolean) => chatWebSocket.sendWebRTCSignal(tid, 'SERVER_MUTE', { mute: m }) };
};
